import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  api,
  type InvoicePayload,
  type Message,
  type MessageKind,
  type PhonePayload,
  type ReplyRef,
} from "../api";
import { useAuth } from "../auth";
import { useCounters } from "../counters";
import { useUsers } from "../useUsers";
import { fmtDate } from "../ui";
import Avatar from "../components/Avatar";
import MessageTicks from "../components/MessageTicks";
import QuotedMessage from "../components/QuotedMessage";
import VoiceRecorder from "../components/VoiceRecorder";
import VoiceBubble from "../components/VoiceBubble";

const KIND_LABEL: Record<MessageKind, string> = {
  general: "Général",
  invoice: "Facture",
  phone:   "Téléphone",
  voice:   "Vocal",
};

type ComposerMode = null | MessageKind;
type View = "inbox" | "trash";

/* ==========================================================================
 * MESSAGES — page principale
 * ========================================================================== */

export default function Messages() {
  const { user } = useAuth();
  const { refresh } = useCounters();
  const users = useUsers();
  const [items, setItems] = useState<Message[]>([]);
  const [composer, setComposer] = useState<ComposerMode>(null);
  const [view, setView] = useState<View>("inbox");
  const [replyTo, setReplyTo] = useState<ReplyRef | null>(null);

  // Filtres
  const [q, setQ] = useState("");
  const [kindFilter, setKindFilter] = useState<MessageKind | "">("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [showSearch, setShowSearch] = useState(false);

  const threadEndRef = useRef<HTMLDivElement>(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (view === "trash") params.set("trashed", "1");
    if (q.trim()) params.set("q", q.trim());
    if (kindFilter) params.set("kind", kindFilter);
    if (from) params.set("from", String(new Date(from).getTime()));
    if (to)   params.set("to",   String(new Date(to + "T23:59:59").getTime()));
    if (unreadOnly) params.set("unread", "1");
    const r = await api<{ messages: Message[] }>(`/messages?${params}`);
    setItems(r.messages.slice().reverse()); // chronologique (vieux en haut)
  }, [q, kindFilter, from, to, unreadOnly, view]);

  useEffect(() => {
    load();
    const t = setInterval(load, 20_000);
    return () => clearInterval(t);
  }, [load]);

  // Intersection observer : marque "lu" les bulles réellement visibles à l'écran
  // (pas celles juste présentes dans le DOM). Debounce 500ms pour batcher.
  const pendingReadRef = useRef<Set<string>>(new Set());
  const debounceRef = useRef<number | null>(null);
  const flushPendingRead = useCallback(async () => {
    if (pendingReadRef.current.size === 0) return;
    const ids = Array.from(pendingReadRef.current);
    pendingReadRef.current.clear();
    try {
      await api("/messages/read-batch", { method: "POST", json: { ids } });
      await refresh();
    } catch { /* silencieux */ }
  }, [refresh]);

  const schedulePush = useCallback((id: string) => {
    pendingReadRef.current.add(id);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(flushPendingRead, 500);
  }, [flushPendingRead]);

  useEffect(() => {
    return () => { if (debounceRef.current) window.clearTimeout(debounceRef.current); };
  }, []);

  async function toggleProcessed(m: Message) {
    if (m.processed_at) {
      await api(`/messages/${m.id}/unprocessed`, { method: "POST" });
    } else {
      await api(`/messages/${m.id}/processed`, { method: "POST" });
    }
    await load();
    await refresh();
  }

  async function softDelete(m: Message) {
    if (!confirm("Supprimer ce message ? Il sera placé dans la corbeille jusqu'au 1er du mois prochain.")) return;
    await api(`/messages/${m.id}`, { method: "DELETE" });
    await load();
    await refresh();
  }

  async function restore(m: Message) {
    await api(`/messages/${m.id}/restore`, { method: "POST" });
    await load();
    await refresh();
  }

  async function emptyTrash() {
    if (!confirm("Vider définitivement la corbeille ? Cette action est irréversible.")) return;
    await api("/messages/empty-trash", { method: "POST" });
    await load();
  }

  function handleReply(m: Message) {
    setReplyTo({
      id: m.id,
      user_id: m.user_id,
      user_name: m.user_name,
      identifier: m.identifier,
      kind: m.kind || "general",
      body: m.body,
    });
    setComposer(m.kind === "voice" || m.kind === "invoice" || m.kind === "phone" ? "general" : "general");
    setTimeout(() => threadEndRef.current?.scrollIntoView({ behavior: "smooth" }), 50);
  }

  function handleJumpTo(id: string) {
    const el = document.getElementById(`msg-${id}`);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      el.classList.add("flash");
      setTimeout(() => el.classList.remove("flash"), 1500);
    }
  }

  // Calcul des "groupes" et séparateurs de date
  const rows = useMemo(() => buildThreadRows(items, user?.id || ""), [items, user?.id]);

  return (
    <>
      <div className="row spread">
        <h2 style={{ margin: 0 }}>{view === "trash" ? "Corbeille" : "Messagerie"}</h2>
        <div className="row" style={{ gap: 6 }}>
          <button className="btn btn-ghost sm" onClick={() => setShowSearch((v) => !v)} title="Rechercher">
            🔍
          </button>
          <button
            className="btn btn-ghost sm"
            onClick={() => setView(view === "trash" ? "inbox" : "trash")}
            title={view === "trash" ? "Retour messages" : "Corbeille"}
          >
            {view === "trash" ? "←" : "🗑"}
          </button>
        </div>
      </div>

      {showSearch && view === "inbox" && (
        <div className="card">
          <input placeholder="Recherche dans le contenu…" value={q} onChange={(e) => setQ(e.target.value)} />
          <label className="section-h" style={{ marginTop: 10 }}>Type</label>
          <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value as MessageKind | "")}>
            <option value="">Tous types</option>
            <option value="general">Général</option>
            <option value="invoice">Facture</option>
            <option value="phone">Téléphone</option>
            <option value="voice">Vocal</option>
          </select>
          <div className="row" style={{ gap: 8, marginTop: 10 }}>
            <div style={{ flex: 1 }}>
              <label className="section-h">Du</label>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label className="section-h">Au</label>
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
          </div>
          <label className="row" style={{ gap: 8, marginTop: 12, cursor: "pointer" }}>
            <input type="checkbox" checked={unreadOnly} onChange={(e) => setUnreadOnly(e.target.checked)} style={{ width: "auto" }} />
            <span>Non lus uniquement</span>
          </label>
          {(q || kindFilter || from || to || unreadOnly) && (
            <button
              className="btn btn-ghost sm"
              style={{ marginTop: 10 }}
              onClick={() => { setQ(""); setKindFilter(""); setFrom(""); setTo(""); setUnreadOnly(false); }}
            >
              Réinitialiser
            </button>
          )}
        </div>
      )}

      {view === "trash" && user?.role === "admin" && items.length > 0 && (
        <button className="btn btn-ghost full" style={{ marginBottom: 10, color: "var(--terra-deep)" }} onClick={emptyTrash}>
          Vider la corbeille maintenant
        </button>
      )}

      {/* Thread */}
      <div className="thread">
        {rows.map((row, i) =>
          row.type === "sep" ? (
            <div key={`sep-${i}`} className="day-sep">{row.label}</div>
          ) : (
            <MessageRow
              key={row.m.id}
              m={row.m}
              meId={user?.id || ""}
              isAdmin={user?.role === "admin"}
              groupStart={row.groupStart}
              queue={row.queue}
              inTrash={view === "trash"}
              onVisible={schedulePush}
              onProcess={toggleProcessed}
              onDelete={softDelete}
              onRestore={restore}
              onReply={handleReply}
              onJumpToReply={handleJumpTo}
            />
          )
        )}
        {items.length === 0 && (
          <p className="muted" style={{ marginTop: 24 }}>
            {view === "trash" ? "Corbeille vide." : "Aucun message."}
          </p>
        )}
        <div ref={threadEndRef} />
      </div>

      {view === "inbox" && (
        <MessageComposerArea
          composer={composer}
          setComposer={setComposer}
          users={users}
          meId={user!.id}
          replyTo={replyTo}
          clearReply={() => setReplyTo(null)}
          onDone={() => { setComposer(null); setReplyTo(null); load(); refresh(); }}
        />
      )}
    </>
  );
}

/* ==========================================================================
 * Helpers : construction des lignes du thread (séparateurs, regroupement)
 * ========================================================================== */

type Row =
  | { type: "sep"; label: string }
  | { type: "msg"; m: Message; groupStart: boolean; queue: boolean };

function formatDayLabel(ts: number): string {
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  const same = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (same(d, today)) return "Aujourd'hui";
  if (same(d, yesterday)) return "Hier";
  return d.toLocaleDateString("fr-FR", { weekday: "long", day: "2-digit", month: "long", year: same(d, today) ? undefined : "numeric" });
}
function sameDay(a: number, b: number): boolean {
  const da = new Date(a), db = new Date(b);
  return da.getFullYear() === db.getFullYear() && da.getMonth() === db.getMonth() && da.getDate() === db.getDate();
}
/** Regroupe messages consécutifs du même auteur dans une fenêtre de 5 min. */
function sameGroup(a: Message, b: Message): boolean {
  if (a.user_id !== b.user_id) return false;
  if (a.recipient_user_id !== b.recipient_user_id) return false;
  return Math.abs(a.created_at - b.created_at) <= 5 * 60 * 1000;
}

function buildThreadRows(items: Message[], _meId: string): Row[] {
  void _meId;
  const rows: Row[] = [];
  for (let i = 0; i < items.length; i++) {
    const m = items[i];
    const prev = items[i - 1];
    const next = items[i + 1];
    // Séparateur de date
    if (!prev || !sameDay(prev.created_at, m.created_at)) {
      rows.push({ type: "sep", label: formatDayLabel(m.created_at) });
    }
    const groupStart = !prev || !sameGroup(prev, m);
    const isLastOfGroup = !next || !sameGroup(m, next);
    rows.push({ type: "msg", m, groupStart, queue: isLastOfGroup });
  }
  return rows;
}

/* ==========================================================================
 * MessageRow — une bulle (incl. avatar, actions, coches, citation)
 * ========================================================================== */

function MessageRow({
  m, meId, isAdmin, groupStart, queue, inTrash,
  onVisible, onProcess, onDelete, onRestore, onReply, onJumpToReply,
}: {
  m: Message;
  meId: string;
  isAdmin: boolean;
  groupStart: boolean;
  queue: boolean;
  inTrash: boolean;
  onVisible: (id: string) => void;
  onProcess: (m: Message) => void;
  onDelete: (m: Message) => void;
  onRestore: (m: Message) => void;
  onReply: (m: Message) => void;
  onJumpToReply: (id: string) => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const mine = m.user_id === meId;
  const isProcessed = !!m.processed_at;
  const isRead = !!m.read_at;
  const initials = m.identifier || m.user_name.slice(0, 2).toUpperCase();
  const isDeferred = mine && m.deliver_at != null && m.deliver_at > Date.now();

  // Intersection observer : dès qu'une bulle destinée à moi (non lue) est visible
  // pendant un petit instant, on notifie le parent pour batch-mark "lu".
  useEffect(() => {
    if (mine || inTrash || isRead) return;
    if (m.recipient_user_id && m.recipient_user_id !== meId) return;
    const el = ref.current;
    if (!el || !("IntersectionObserver" in window)) {
      onVisible(m.id);
      return;
    }
    const io = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio > 0.5) {
          onVisible(m.id);
          io.disconnect();
          break;
        }
      }
    }, { threshold: 0.5 });
    io.observe(el);
    return () => io.disconnect();
  }, [mine, inTrash, isRead, m.id, m.recipient_user_id, meId, onVisible]);

  const showAuthor = groupStart;

  return (
    <div
      id={`msg-${m.id}`}
      ref={ref}
      className={`bubble-wrap ${mine ? "me" : ""} ${groupStart ? "group-start" : ""}`}
    >
      {groupStart ? (
        <Avatar initials={initials} me={mine} title={m.user_name} />
      ) : (
        <span className="avatar placeholder" aria-hidden>·</span>
      )}

      <div
        className={`bubble ${queue ? "queue" : ""}`}
        style={{ opacity: inTrash ? 0.5 : 1 }}
      >
        {/* Actions flottantes */}
        {!inTrash && (
          <div className="actions">
            <button onClick={() => onReply(m)} title="Répondre">↶</button>
            {!mine && (
              <button
                className="success"
                onClick={() => onProcess(m)}
                title={isProcessed ? "Rouvrir" : "Marquer traité"}
              >
                {isProcessed ? "↻" : "✓"}
              </button>
            )}
            {(mine || isAdmin) && (
              <button className="danger" onClick={() => onDelete(m)} title="Supprimer">
                🗑
              </button>
            )}
          </div>
        )}
        {inTrash && (
          <div className="actions">
            <button onClick={() => onRestore(m)} title="Restaurer">↺</button>
          </div>
        )}

        {showAuthor && (
          <div className="author">
            {m.user_name}
            {m.kind && m.kind !== "general" && (
              <span className="kind-tag">{KIND_LABEL[m.kind]}</span>
            )}
            {m.recipient_identifier && (
              <span style={{ fontSize: "0.7rem", color: "var(--ink-mute)" }}>
                → {m.recipient_identifier}
              </span>
            )}
          </div>
        )}

        {/* Citation si c'est une réponse */}
        {m.reply_to && (
          <QuotedMessage reply={m.reply_to} onJump={onJumpToReply} />
        )}

        <div className="body">
          {m.kind === "voice"                    ? <VoiceBubble message={m} /> :
           m.kind === "invoice" && m.payload ? <InvoiceView p={m.payload as InvoicePayload} /> :
           m.kind === "phone"   && m.payload ? <PhoneView   p={m.payload as PhonePayload}   /> :
           m.body}
        </div>

        <div className="footer">
          {!mine && isRead && !isProcessed && <span className="state">en cours</span>}
          {!mine && isProcessed && <span className="state processed">traité</span>}
          {isDeferred && (
            <span style={{ color: "var(--terra-deep)", fontStyle: "italic" }}>
              différé · droit à la déconnexion
            </span>
          )}
          {inTrash && m.deleted_at ? <span>suppr. {fmtDate(m.deleted_at)}</span> : null}
          <span>{fmtDate(m.created_at)}</span>
          <MessageTicks message={m} meId={meId} />
        </div>
      </div>
    </div>
  );
}

/* ==========================================================================
 * Zone composer (sélecteur de type + composer actif + bandeau reply)
 * ========================================================================== */

function MessageComposerArea({
  composer, setComposer, users, meId, replyTo, clearReply, onDone,
}: {
  composer: ComposerMode;
  setComposer: (v: ComposerMode) => void;
  users: { id: string; identifier: string; display_name: string }[];
  meId: string;
  replyTo: ReplyRef | null;
  clearReply: () => void;
  onDone: () => void;
}) {
  const kinds: { k: MessageKind; label: string }[] = [
    { k: "general", label: "Message" },
    { k: "invoice", label: "Facture" },
    { k: "phone",   label: "Téléphone" },
    { k: "voice",   label: "Vocal" },
  ];

  return (
    <>
      {!composer && (
        <div className="kind-tabs">
          {kinds.map((t) => (
            <button key={t.k} onClick={() => setComposer(t.k)}>{t.label}</button>
          ))}
        </div>
      )}

      {replyTo && composer && (
        <div className="composer-reply-bar" style={{ marginBottom: 8 }}>
          <QuotedMessage reply={replyTo} onCancel={clearReply} />
        </div>
      )}

      {composer === "general" && <GeneralComposer users={users} meId={meId} replyTo={replyTo} onDone={onDone} onCancel={() => { setComposer(null); clearReply(); }} />}
      {composer === "invoice" && <InvoiceComposer users={users} meId={meId} replyTo={replyTo} onDone={onDone} onCancel={() => { setComposer(null); clearReply(); }} />}
      {composer === "phone"   && <PhoneComposer   users={users} meId={meId} replyTo={replyTo} onDone={onDone} onCancel={() => { setComposer(null); clearReply(); }} />}
      {composer === "voice"   && <VoiceComposer   users={users} meId={meId} replyTo={replyTo} onDone={onDone} onCancel={() => { setComposer(null); clearReply(); }} />}
    </>
  );
}

/* ==========================================================================
 * Composers (général/facture/téléphone/vocal)
 * ========================================================================== */

interface ComposerProps {
  users: { id: string; identifier: string; display_name: string }[];
  meId: string;
  replyTo: ReplyRef | null;
  onDone: () => void;
  onCancel: () => void;
}

function RecipientPicker({
  value, onChange, users, meId,
}: {
  value: string | "";
  onChange: (v: string | "") => void;
  users: { id: string; identifier: string; display_name: string }[];
  meId: string;
}) {
  return (
    <>
      <label className="section-h" style={{ marginTop: 0 }}>À</label>
      <select value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">Tout le monde</option>
        {users.filter((u) => u.id !== meId).map((u) => (
          <option key={u.id} value={u.id}>{u.identifier} — {u.display_name}</option>
        ))}
      </select>
    </>
  );
}

function SendIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
         strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M22 2L11 13" />
      <path d="M22 2l-7 20-4-9-9-4 20-7z" />
    </svg>
  );
}

function GeneralComposer({ users, meId, replyTo, onDone, onCancel }: ComposerProps) {
  const [body, setBody] = useState("");
  const [recipient, setRecipient] = useState<string | "">("");
  const [busy, setBusy] = useState(false);

  async function send() {
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api("/messages", {
        method: "POST",
        json: {
          kind: "general",
          body,
          recipient_user_id: recipient || null,
          reply_to_message_id: replyTo?.id || null,
        },
      });
      onDone();
    } finally { setBusy(false); }
  }

  return (
    <div className="composer">
      <div className="card" style={{ marginBottom: 0, padding: 12 }}>
        <RecipientPicker value={recipient} onChange={setRecipient} users={users} meId={meId} />
      </div>
      <div className="input-row">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={1}
          placeholder={replyTo ? "Répondre…" : "Écrire un message…"}
          autoFocus
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); }
          }}
        />
        <button className="send" onClick={send} disabled={busy || !body.trim()} aria-label="Envoyer">
          <SendIcon />
        </button>
      </div>
      <button className="btn btn-ghost sm" onClick={onCancel} style={{ alignSelf: "flex-start" }}>Annuler</button>
    </div>
  );
}

function InvoiceComposer({ users, meId, replyTo, onDone, onCancel }: ComposerProps) {
  const [recipient, setRecipient] = useState<string | "">("");
  const [client, setClient] = useState("");
  const [label, setLabel] = useState("");
  const [amount, setAmount] = useState("");
  const [hasFees, setHasFees] = useState(false);
  const [fees, setFees] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null);
    const cents = Math.round(parseFloat(amount.replace(",", ".")) * 100);
    if (!cents || isNaN(cents)) return setErr("Montant HT invalide");
    let feesCents: number | undefined;
    if (hasFees) {
      feesCents = Math.round(parseFloat(fees.replace(",", ".")) * 100);
      if (!feesCents || isNaN(feesCents)) return setErr("Montant des frais invalide");
    }
    setBusy(true);
    try {
      const payload: InvoicePayload = {
        client: client.trim(),
        label: label.trim(),
        amount_ht_cents: cents,
        has_fees: hasFees,
        fees_cents: feesCents ?? null,
      };
      await api("/messages", {
        method: "POST",
        json: {
          kind: "invoice",
          payload,
          recipient_user_id: recipient || null,
          reply_to_message_id: replyTo?.id || null,
        },
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="section-h" style={{ margin: 0 }}>Facture à émettre</div>
      <RecipientPicker value={recipient} onChange={setRecipient} users={users} meId={meId} />
      <label className="section-h">Client</label>
      <input value={client} onChange={(e) => setClient(e.target.value)} required autoFocus />
      <label className="section-h">Libellé</label>
      <input value={label} onChange={(e) => setLabel(e.target.value)} required />
      <label className="section-h">Montant HT (€)</label>
      <input type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0,00" required />
      <label className="row" style={{ gap: 8, marginTop: 12, cursor: "pointer" }}>
        <input type="checkbox" checked={hasFees} onChange={(e) => setHasFees(e.target.checked)} style={{ width: "auto" }} />
        <span>Frais à refacturer</span>
      </label>
      {hasFees && (
        <>
          <label className="section-h">Montant des frais (€)</label>
          <input type="text" inputMode="decimal" value={fees} onChange={(e) => setFees(e.target.value)} placeholder="0,00" />
        </>
      )}
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary sm" onClick={send} disabled={busy}>Envoyer</button>
        <button className="btn btn-ghost sm" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

function PhoneComposer({ users, meId, replyTo, onDone, onCancel }: ComposerProps) {
  const nowDt = new Date();
  const localISO = new Date(nowDt.getTime() - nowDt.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
  const [recipient, setRecipient] = useState<string | "">("");
  const [clientName, setClientName] = useState("");
  const [callTime, setCallTime] = useState(localISO);
  const [file, setFile] = useState("");
  const [callback, setCallback] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function send() {
    setErr(null); setBusy(true);
    try {
      const payload: PhonePayload = {
        client_name: clientName.trim(),
        call_time: new Date(callTime).getTime(),
        file: file.trim(),
        callback_number: callback.trim() || null,
        message: message.trim(),
      };
      await api("/messages", {
        method: "POST",
        json: {
          kind: "phone",
          payload,
          recipient_user_id: recipient || null,
          reply_to_message_id: replyTo?.id || null,
        },
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally { setBusy(false); }
  }

  return (
    <div className="card">
      <div className="section-h" style={{ margin: 0 }}>Message téléphonique</div>
      <RecipientPicker value={recipient} onChange={setRecipient} users={users} meId={meId} />
      <label className="section-h">Nom du client</label>
      <input value={clientName} onChange={(e) => setClientName(e.target.value)} required autoFocus />
      <label className="section-h">Heure de l'appel</label>
      <input type="datetime-local" value={callTime} onChange={(e) => setCallTime(e.target.value)} required />
      <label className="section-h">Dossier</label>
      <input value={file} onChange={(e) => setFile(e.target.value)} required />
      <label className="section-h">N° de rappel (optionnel)</label>
      <input type="tel" value={callback} onChange={(e) => setCallback(e.target.value)} />
      <label className="section-h">Message</label>
      <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} required />
      {err && <div className="error" style={{ marginTop: 10 }}>{err}</div>}
      <div className="row" style={{ gap: 8, marginTop: 12 }}>
        <button className="btn btn-primary sm" onClick={send} disabled={busy}>Envoyer</button>
        <button className="btn btn-ghost sm" onClick={onCancel}>Annuler</button>
      </div>
    </div>
  );
}

function VoiceComposer({ users, meId, replyTo, onDone, onCancel }: ComposerProps) {
  const [recipient, setRecipient] = useState<string | "">("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function onUploaded(info: { audio_r2_key: string; audio_duration_sec: number }) {
    setBusy(true);
    setErr(null);
    try {
      await api("/messages/voice", {
        method: "POST",
        json: {
          recipient_user_id: recipient || null,
          audio_r2_key: info.audio_r2_key,
          audio_duration_sec: info.audio_duration_sec,
          reply_to_message_id: replyTo?.id || null,
        },
      });
      onDone();
    } catch (e: unknown) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="card" style={{ marginBottom: 8 }}>
        <RecipientPicker value={recipient} onChange={setRecipient} users={users} meId={meId} />
        {err && <div className="error" style={{ marginTop: 8 }}>{err}</div>}
        {busy && <p className="muted" style={{ marginTop: 8 }}>Envoi…</p>}
      </div>
      {!busy && <VoiceRecorder onUploaded={onUploaded} onCancel={onCancel} />}
    </>
  );
}

/* ==========================================================================
 * Affichages structurés (Facture / Téléphone)
 * ========================================================================== */

function InvoiceView({ p }: { p: InvoicePayload }) {
  return (
    <div>
      <div><b>📄 Facture à émettre</b></div>
      <div>Client : {p.client}</div>
      <div>{p.label}</div>
      <div>Montant HT : <b>{(p.amount_ht_cents / 100).toFixed(2)} €</b></div>
      {p.has_fees && p.fees_cents != null && (
        <div>Frais : {(p.fees_cents / 100).toFixed(2)} €</div>
      )}
    </div>
  );
}

function PhoneView({ p }: { p: PhonePayload }) {
  const t = new Date(p.call_time);
  return (
    <div>
      <div><b>📞 {p.client_name}</b></div>
      <div className="tiny">Appel : {t.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</div>
      <div>Dossier : {p.file}</div>
      {p.callback_number && <div>Rappel : <a href={`tel:${p.callback_number}`}>{p.callback_number}</a></div>}
      <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{p.message}</div>
    </div>
  );
}
